import {
  collection,
  doc,
  getDocFromServer,
  getDocsFromServer,
  runTransaction,
  setDoc,
} from 'firebase/firestore'
import { assertFirestoreEnvironmentForWrite, firestore } from './firebase'
import {
  normalizeFieldKey,
  type CanonicalFieldDefinition,
} from './deviceTypeCanonicalizer'
import type { DeviceTypeRegistryEntry } from './deviceTypeRegistryStore'

export interface DeviceTypeSchemaSnapshot {
  deviceType: string
  versionId: string
  schemaVersion: number
  canonicalFields: CanonicalFieldDefinition[]
  traitMap: Record<string, string>
  attributeMap: Record<string, string>
  sensorDataMap?: Record<string, string>
  commandMap: Record<string, string>
  status: 'draft' | 'published' | 'deprecated'
  createdAt: string
  createdBy?: string
  publishedAt?: string
  publishedBy?: string
  notes?: string
}

export interface DeviceTypeMeta {
  deviceType: string
  displayName?: string
  // Device-type icon pool for card media icon source.
  // Always normalized to 10 slots (slot1 ~ slot10); default uses slot1.
  iconSlots?: Array<{ id: string; dataUrl?: string }>
  iconUploads?: Array<{ id: string; dataUrl: string }>
  defaultIconSlotId?: string
  iconName?: string
  iconImageDataUrl?: string
  publishedVersionId?: string
  draftUpdatedAt?: string
  publishedUpdatedAt?: string
  updatedAt?: string
  // Canonical capability snapshot (single source for UTM UI)
  traits?: string[]
  attributes?: string[]
  states?: string[]
  traitAttributeMap?: Record<string, string[]>
  optionValues?: Record<string, string[]>
  labelMap?: Record<string, string>
  commandSchemas?: Record<string, Record<string, unknown>>
  sampleSources?: string[]
  sensorClassesV1?: string[]
  resolvedDeviceTypeV1?: string
  classificationVersion?: 'v1'
  keyCounts?: {
    traits: number
    attributes: number
    states?: number
  }
}

export interface DeviceTypeVersionDiff {
  addedFields: string[]
  removedFields: string[]
  changedAliases: string[]
}

export interface DeviceTypeCapabilitySnapshot {
  deviceType: string
  traits: string[]
  attributes: string[]
  states: string[]
  sensorClassesV1?: string[]
  resolvedDeviceTypeV1?: string
  classificationVersion?: 'v1'
  traitAttributeMap?: Record<string, string[]>
  optionValues?: Record<string, string[]>
  labelMap?: Record<string, string>
  commandSchemas?: Record<string, Record<string, unknown>>
  sampleSources?: string[]
  updatedAt?: string
  version?: string
  hash?: string
}

function ensureFirestore() {
  if (!firestore) {
    throw new Error('Firebase is not configured. Check VITE_FIREBASE_* env values.')
  }
  return firestore
}

function getRefs() {
  const db = ensureFirestore()
  const root = collection(doc(db, 'appfactory', 'config'), 'deviceTypes')
  return {
    root,
    doc: (deviceType: string) => doc(root, deviceType),
    draft: (deviceType: string) => doc(root, deviceType, 'draft', 'current'),
    published: (deviceType: string) => doc(root, deviceType, 'published', 'current'),
    versions: (deviceType: string) => collection(root, deviceType, 'versions'),
    version: (deviceType: string, versionId: string) => doc(root, deviceType, 'versions', versionId),
  }
}

const nowIso = () => new Date().toISOString()
const ICON_SLOT_COUNT = 10
const DEFAULT_ICON_SLOT_ID = 'slot1'
const EXCLUDED_TRAITS = new Set(['DetectorStates'])

function newVersionId() {
  return `v_${Date.now()}`
}

function toSortedUnique(values: string[] = []): string[] {
  return Array.from(
    new Set(
      values
        .map((item) => String(item || '').trim())
        .filter(Boolean)
    )
  ).sort((a, b) => a.localeCompare(b))
}

function toTrimmedString(value: unknown): string {
  return String(value || '').trim()
}

function asOptionalString(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined
  const normalized = value.trim()
  return normalized || undefined
}

function parseSlotIndex(slotId: string): number {
  const match = /^slot(\d+)$/i.exec(slotId)
  return match ? Number(match[1]) : Number.POSITIVE_INFINITY
}

function normalizeDeviceTypeIconSlots(
  iconSlotsValue: unknown,
  iconUploadsValue: unknown,
  legacyIconImageDataUrl?: string
): Array<{ id: string; dataUrl?: string }> {
  const slotMap = new Map<string, { id: string; dataUrl?: string }>()
  const applySlot = (rawId: unknown, rawDataUrl: unknown) => {
    const id = toTrimmedString(rawId)
    if (!id) return
    const dataUrl = toTrimmedString(rawDataUrl)
    const existing = slotMap.get(id)
    if (dataUrl) {
      slotMap.set(id, { id, dataUrl })
      return
    }
    if (!existing) {
      slotMap.set(id, { id })
    }
  }

  if (Array.isArray(iconSlotsValue)) {
    for (const slot of iconSlotsValue as Array<{ id?: unknown; dataUrl?: unknown }>) {
      applySlot(slot?.id, slot?.dataUrl)
    }
  }

  // Backward compatibility: migrate legacy iconUploads field into icon slots.
  if (Array.isArray(iconUploadsValue)) {
    for (const slot of iconUploadsValue as Array<{ id?: unknown; dataUrl?: unknown }>) {
      applySlot(slot?.id, slot?.dataUrl)
    }
  }

  for (let i = 1; i <= ICON_SLOT_COUNT; i += 1) {
    const id = `slot${i}`
    if (!slotMap.has(id)) slotMap.set(id, { id })
  }

  const slot1 = slotMap.get(DEFAULT_ICON_SLOT_ID)
  const legacy = toTrimmedString(legacyIconImageDataUrl)
  if (legacy && slot1 && !toTrimmedString(slot1.dataUrl)) {
    slotMap.set(DEFAULT_ICON_SLOT_ID, { ...slot1, dataUrl: legacy })
  }

  return Array.from(slotMap.values()).sort((a, b) => parseSlotIndex(a.id) - parseSlotIndex(b.id))
}

function toStrictRawToCanonicalMap(entries: string[]) {
  const map: Record<string, string> = {}
  toSortedUnique(entries).forEach((entry) => {
    const canonical = normalizeFieldKey(entry)
    if (canonical) map[entry] = canonical
  })
  return map
}

function toStrictCanonicalFields(observedRawKeys: string[]): CanonicalFieldDefinition[] {
  return toSortedUnique(observedRawKeys)
    .map((rawKey) => normalizeFieldKey(rawKey))
    .filter(Boolean)
    .map((name) => ({
      name,
      aliases: [],
      // Strict mode: no inferred category/valueType.
      category: 'custom',
      valueType: 'string',
      displayDefault: 'text',
    }))
}

export function buildStrictDraftFromRegistryEntry(
  entry: DeviceTypeRegistryEntry,
  actor = 'system'
): DeviceTypeSchemaSnapshot {
  const traitKeys = toSortedUnique(entry.traits || [])
  const attributeKeys = toSortedUnique(entry.attributes || [])
  const canonicalFields = toStrictCanonicalFields([...traitKeys, ...attributeKeys])

  return {
    deviceType: entry.deviceType,
    versionId: 'draft',
    schemaVersion: 1,
    canonicalFields,
    traitMap: toStrictRawToCanonicalMap(traitKeys),
    attributeMap: toStrictRawToCanonicalMap(attributeKeys),
    sensorDataMap: {},
    commandMap: {},
    status: 'draft',
    createdAt: nowIso(),
    createdBy: actor,
  }
}

function capabilityRefs(deviceType: string) {
  const refs = getRefs()
  return {
    current: doc(refs.root, deviceType, 'capability', 'current'),
  }
}

function toArray(values: unknown): string[] {
  if (!Array.isArray(values)) return []
  return toSortedUnique(values.map((item) => String(item || '').trim()).filter(Boolean))
}

function normalizeStringArrayMap(value: unknown): Record<string, string[]> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {}
  const next: Record<string, string[]> = {}
  for (const [rawKey, rawValues] of Object.entries(value as Record<string, unknown>)) {
    const key = String(rawKey || '').trim()
    if (!key) continue
    const values = toArray(rawValues)
    if (values.length > 0) next[key] = values
  }
  return next
}

function normalizeStringMap(value: unknown): Record<string, string> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {}
  const next: Record<string, string> = {}
  for (const [rawKey, rawValue] of Object.entries(value as Record<string, unknown>)) {
    const key = String(rawKey || '').trim()
    const mapped = String(rawValue || '').trim()
    if (!key || !mapped) continue
    next[key] = mapped
  }
  return next
}

export function buildCapabilitySnapshotFromRegistryEntry(
  entry: DeviceTypeRegistryEntry
): DeviceTypeCapabilitySnapshot {
  const sensorClasses = toArray(entry.sensorClassesV1)
  const resolvedDeviceType = String(entry.resolvedDeviceTypeV1 || '').trim() || undefined
  const classificationVersion = String(entry.classificationVersion || '').trim() === 'v1' ? 'v1' : undefined
  return {
    deviceType: entry.deviceType,
    traits: toArray(entry.traits),
    attributes: toArray(entry.attributes),
    states: toArray(entry.states),
    sensorClassesV1: sensorClasses.length > 0 ? sensorClasses : undefined,
    resolvedDeviceTypeV1: resolvedDeviceType,
    classificationVersion,
    traitAttributeMap: normalizeStringArrayMap(entry.traitAttributeMap),
    optionValues: normalizeStringArrayMap(entry.optionValues),
    labelMap: normalizeStringMap(entry.labelMap),
    commandSchemas: (entry.commandSchemas && typeof entry.commandSchemas === 'object' && !Array.isArray(entry.commandSchemas))
      ? entry.commandSchemas
      : {},
    sampleSources: toArray(entry.sampleSources),
    updatedAt: nowIso(),
    version: 'v1',
  }
}

export async function loadAllDeviceTypeCapabilitySnapshots(): Promise<Record<string, DeviceTypeCapabilitySnapshot>> {
  const refs = getRefs()
  const typeDocs = await getDocsFromServer(refs.root)
  const result: Record<string, DeviceTypeCapabilitySnapshot> = {}
  await Promise.all(
    typeDocs.docs.map(async (item) => {
      const deviceType = item.id
      const currentRef = capabilityRefs(deviceType).current
      const snap = await getDocFromServer(currentRef)
      if (!snap.exists()) return
      const data = snap.data() as Partial<DeviceTypeCapabilitySnapshot>
      result[deviceType] = {
        deviceType,
        traits: toArray(data.traits),
        attributes: toArray(data.attributes),
        states: toArray(data.states),
        sensorClassesV1: toArray(data.sensorClassesV1),
        resolvedDeviceTypeV1: String(data.resolvedDeviceTypeV1 || '').trim() || undefined,
        classificationVersion: String(data.classificationVersion || '').trim() === 'v1' ? 'v1' : undefined,
        traitAttributeMap: normalizeStringArrayMap(data.traitAttributeMap),
        optionValues: normalizeStringArrayMap(data.optionValues),
        labelMap: normalizeStringMap(data.labelMap),
        commandSchemas: (data.commandSchemas && typeof data.commandSchemas === 'object' && !Array.isArray(data.commandSchemas))
          ? data.commandSchemas
          : {},
        sampleSources: toArray(data.sampleSources),
        updatedAt: data.updatedAt,
        version: data.version,
        hash: data.hash,
      }
    })
  )
  return result
}

export async function backfillCapabilitySnapshotsFromRegistry(
  entries: DeviceTypeRegistryEntry[],
  actor = 'system'
): Promise<{ created: number; updated: number; failed: string[] }> {
  assertFirestoreEnvironmentForWrite('backfillCapabilitySnapshotsFromRegistry')
  const outcome = { created: 0, updated: 0, failed: [] as string[] }
  await Promise.all(
    entries.map(async (entry) => {
      try {
        const refs = capabilityRefs(entry.deviceType)
        const snapshot = buildCapabilitySnapshotFromRegistryEntry(entry)
        const current = await getDocFromServer(refs.current)
        await setDoc(refs.current, {
          ...snapshot,
          updatedAt: nowIso(),
          updatedBy: actor,
        }, { merge: true })
        if (current.exists()) outcome.updated += 1
        else outcome.created += 1
      } catch {
        outcome.failed.push(entry.deviceType)
      }
    })
  )
  return outcome
}

function fieldsOf(snapshot?: Partial<DeviceTypeSchemaSnapshot>) {
  return (snapshot?.canonicalFields || []).map((field) => field.name)
}

export async function listDeviceTypesMeta(): Promise<DeviceTypeMeta[]> {
  const { root } = getRefs()
  const snap = await getDocsFromServer(root)
  return snap.docs
    .map((item) => {
      const data = item.data() as Record<string, unknown>
      const classificationVersion: 'v1' | undefined =
        String(data.classificationVersion || '').trim() === 'v1' ? 'v1' : undefined
      const iconImageDataUrl = asOptionalString(data.iconImageDataUrl)
      const iconSlots = normalizeDeviceTypeIconSlots(data.iconSlots, data.iconUploads, iconImageDataUrl)
      return {
        deviceType: item.id,
        displayName: asOptionalString(data.displayName),
        iconSlots,
        // Keep legacy shape so existing UI can use slot list without migrations.
        iconUploads: iconSlots.map((slot) => ({ id: slot.id, dataUrl: toTrimmedString(slot.dataUrl) })),
        defaultIconSlotId: asOptionalString(data.defaultIconSlotId) || DEFAULT_ICON_SLOT_ID,
        iconName: asOptionalString(data.iconName),
        iconImageDataUrl,
        publishedVersionId: asOptionalString(data.publishedVersionId),
        draftUpdatedAt: asOptionalString(data.draftUpdatedAt),
        publishedUpdatedAt: asOptionalString(data.publishedUpdatedAt),
        updatedAt: asOptionalString(data.updatedAt),
        traits: toArray(data.traits),
        attributes: toArray(data.attributes),
        states: toArray(data.states),
        traitAttributeMap: normalizeStringArrayMap(data.traitAttributeMap),
        optionValues: normalizeStringArrayMap(data.optionValues),
        labelMap: normalizeStringMap(data.labelMap),
        commandSchemas: (data.commandSchemas && typeof data.commandSchemas === 'object' && !Array.isArray(data.commandSchemas))
          ? (data.commandSchemas as Record<string, Record<string, unknown>>)
          : {},
        sampleSources: toArray(data.sampleSources),
        sensorClassesV1: toArray(data.sensorClassesV1),
        resolvedDeviceTypeV1: asOptionalString(data.resolvedDeviceTypeV1),
        classificationVersion,
        keyCounts: {
          traits: toArray(data.traits).length,
          attributes: toArray(data.attributes).length,
          states: toArray(data.states).length,
        },
      }
    })
    .sort((a, b) => a.deviceType.localeCompare(b.deviceType))
}

export function toRegistryEntryFromDeviceTypeMeta(meta: DeviceTypeMeta): DeviceTypeRegistryEntry {
  const traits = toArray(meta.traits).filter((trait) => !EXCLUDED_TRAITS.has(trait))
  const attributes = toArray(meta.attributes)
  const states = toArray(meta.states)
  return {
    deviceType: meta.deviceType,
    traits,
    attributes,
    states,
    traitAttributeMap: normalizeStringArrayMap(meta.traitAttributeMap),
    optionValues: normalizeStringArrayMap(meta.optionValues),
    labelMap: normalizeStringMap(meta.labelMap),
    commandSchemas: (meta.commandSchemas && typeof meta.commandSchemas === 'object' && !Array.isArray(meta.commandSchemas))
      ? meta.commandSchemas
      : {},
    sampleSources: toArray(meta.sampleSources).slice(0, 20),
    sensorClassesV1: toArray(meta.sensorClassesV1),
    resolvedDeviceTypeV1: asOptionalString(meta.resolvedDeviceTypeV1),
    classificationVersion: String(meta.classificationVersion || '').trim() === 'v1' ? 'v1' : undefined,
    source: 'queryDevices',
    updatedAt: asOptionalString(meta.updatedAt) || nowIso(),
    keyCounts: {
      traits: traits.length,
      attributes: attributes.length,
      states: states.length,
    },
  }
}

export async function listDeviceTypeEntriesFromDeviceTypes(
  sourceMetaList?: DeviceTypeMeta[]
): Promise<DeviceTypeRegistryEntry[]> {
  const metaList = sourceMetaList || await listDeviceTypesMeta()
  const entries = await Promise.all(
    metaList.map(async (meta) => {
      const base = toRegistryEntryFromDeviceTypeMeta(meta)
      if (base.traits.length > 0 && base.attributes.length > 0) return base

      const draft = await loadDeviceTypeDraft(meta.deviceType).catch(() => null)
      if (!draft) return base

      const draftTraits = toSortedUnique(Object.keys(draft.traitMap || {}))
        .filter((trait) => !EXCLUDED_TRAITS.has(trait))
      const draftAttributes = toSortedUnique(Object.keys(draft.attributeMap || {}))

      const traits = base.traits.length > 0 ? base.traits : draftTraits
      const attributes = base.attributes.length > 0 ? base.attributes : draftAttributes

      return {
        ...base,
        traits,
        attributes,
        keyCounts: {
          traits: traits.length,
          attributes: attributes.length,
          states: base.states.length,
        },
      }
    })
  )

  return entries.sort((a, b) => a.deviceType.localeCompare(b.deviceType))
}

export async function upsertDeviceTypeMetaFromRegistryEntry(
  entry: DeviceTypeRegistryEntry,
  actor = 'sync'
): Promise<void> {
  assertFirestoreEnvironmentForWrite('upsertDeviceTypeMetaFromRegistryEntry')
  const refs = getRefs()
  const timestamp = nowIso()
  const traits = toArray(entry.traits)
  const attributes = toArray(entry.attributes)
  const states = toArray(entry.states)
  const payload = {
    deviceType: entry.deviceType,
    traits,
    attributes,
    states,
    traitAttributeMap: normalizeStringArrayMap(entry.traitAttributeMap),
    optionValues: normalizeStringArrayMap(entry.optionValues),
    labelMap: normalizeStringMap(entry.labelMap),
    commandSchemas: (entry.commandSchemas && typeof entry.commandSchemas === 'object' && !Array.isArray(entry.commandSchemas))
      ? entry.commandSchemas
      : {},
    sampleSources: toArray(entry.sampleSources).slice(0, 20),
    sensorClassesV1: toArray(entry.sensorClassesV1),
    resolvedDeviceTypeV1: asOptionalString(entry.resolvedDeviceTypeV1),
    classificationVersion: String(entry.classificationVersion || '').trim() === 'v1' ? 'v1' : undefined,
    keyCounts: {
      traits: traits.length,
      attributes: attributes.length,
      states: states.length,
    },
    updatedAt: timestamp,
    updatedBy: actor,
  }
  const safePayload = Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== undefined)
  )
  await setDoc(refs.doc(entry.deviceType), safePayload, { merge: true })
}

export async function loadDeviceTypeDraft(deviceType: string): Promise<DeviceTypeSchemaSnapshot | null> {
  const { draft } = getRefs()
  const snap = await getDocFromServer(draft(deviceType))
  if (!snap.exists()) return null
  return snap.data() as DeviceTypeSchemaSnapshot
}

export async function loadPublishedDeviceTypeSchema(deviceType: string): Promise<DeviceTypeSchemaSnapshot | null> {
  const { published } = getRefs()
  const snap = await getDocFromServer(published(deviceType))
  if (!snap.exists()) return null
  return snap.data() as DeviceTypeSchemaSnapshot
}

export async function loadAllPublishedDeviceTypeSchemas(): Promise<Record<string, DeviceTypeSchemaSnapshot>> {
  const refs = getRefs()
  const types = await listDeviceTypesMeta()
  const result: Record<string, DeviceTypeSchemaSnapshot> = {}
  await Promise.all(
    types.map(async (item) => {
      const snap = await getDocFromServer(refs.published(item.deviceType))
      if (snap.exists()) {
        result[item.deviceType] = snap.data() as DeviceTypeSchemaSnapshot
      }
    })
  )
  return result
}

export async function saveDeviceTypeDraft(
  deviceType: string,
  draftSchema: DeviceTypeSchemaSnapshot,
  actor = 'system'
): Promise<void> {
  assertFirestoreEnvironmentForWrite('saveDeviceTypeDraft')
  const refs = getRefs()
  const timestamp = nowIso()
  const payload: DeviceTypeSchemaSnapshot = {
    ...draftSchema,
    deviceType,
    status: 'draft',
    versionId: draftSchema.versionId || 'draft',
    schemaVersion: draftSchema.schemaVersion || 1,
    createdAt: draftSchema.createdAt || timestamp,
    createdBy: draftSchema.createdBy || actor,
  }
  // Draft save should fully replace the current document to avoid stale fields.
  await setDoc(refs.draft(deviceType), payload, { merge: false })
  await setDoc(refs.doc(deviceType), {
    deviceType,
    updatedAt: timestamp,
    draftUpdatedAt: timestamp,
  }, { merge: true })
}

export async function generateDraftFromRegistry(
  entry: DeviceTypeRegistryEntry,
  actor = 'system'
): Promise<DeviceTypeSchemaSnapshot> {
  const draft = buildStrictDraftFromRegistryEntry(entry, actor)
  await saveDeviceTypeDraft(entry.deviceType, draft, actor)
  return draft
}

export async function backfillDraftsFromRegistry(
  entries: DeviceTypeRegistryEntry[],
  actor = 'system'
): Promise<{ created: number; skipped: number; failed: string[] }> {
  const checks = await Promise.all(entries.map(async (entry) => {
    const existing = await loadDeviceTypeDraft(entry.deviceType)
    return { entry, hasDraft: Boolean(existing) }
  }))

  const missing = checks.filter((item) => !item.hasDraft).map((item) => item.entry)
  if (missing.length === 0) {
    return { created: 0, skipped: checks.length, failed: [] }
  }

  const results = await Promise.all(
    missing.map(async (entry) => {
      try {
        await generateDraftFromRegistry(entry, actor)
        return { deviceType: entry.deviceType, ok: true as const }
      } catch {
        return { deviceType: entry.deviceType, ok: false as const }
      }
    })
  )

  const failed = results.filter((item) => !item.ok).map((item) => item.deviceType)
  const created = results.length - failed.length
  return {
    created,
    skipped: checks.length - missing.length,
    failed,
  }
}

export async function publishDeviceTypeVersion(
  deviceType: string,
  actor = 'system',
  notes = ''
): Promise<DeviceTypeSchemaSnapshot> {
  assertFirestoreEnvironmentForWrite('publishDeviceTypeVersion')
  const db = ensureFirestore()
  const refs = getRefs()
  return runTransaction(db, async (tx) => {
    const draftRef = refs.draft(deviceType)
    const draftSnap = await tx.get(draftRef)
    if (!draftSnap.exists()) {
      throw new Error(`Draft not found for ${deviceType}`)
    }
    const draft = draftSnap.data() as DeviceTypeSchemaSnapshot
    const versionId = newVersionId()
    const timestamp = nowIso()
    const snapshot: DeviceTypeSchemaSnapshot = {
      ...draft,
      versionId,
      status: 'published',
      publishedAt: timestamp,
      publishedBy: actor,
      notes,
    }

    tx.set(refs.version(deviceType, versionId), snapshot, { merge: false })
    tx.set(refs.published(deviceType), snapshot, { merge: true })
    tx.set(refs.doc(deviceType), {
      deviceType,
      publishedVersionId: versionId,
      updatedAt: timestamp,
      publishedUpdatedAt: timestamp,
    }, { merge: true })
    return snapshot
  })
}

export async function listDeviceTypeVersions(deviceType: string): Promise<DeviceTypeSchemaSnapshot[]> {
  const { versions } = getRefs()
  const snap = await getDocsFromServer(versions(deviceType))
  return snap.docs
    .map((item) => item.data() as DeviceTypeSchemaSnapshot)
    .sort((a, b) => (b.publishedAt || b.createdAt || '').localeCompare(a.publishedAt || a.createdAt || ''))
}

export async function diffDeviceTypeVersions(
  deviceType: string,
  fromVersionId: string,
  toVersionId: string
): Promise<DeviceTypeVersionDiff> {
  const refs = getRefs()
  const [fromSnap, toSnap] = await Promise.all([
    getDocFromServer(refs.version(deviceType, fromVersionId)),
    getDocFromServer(refs.version(deviceType, toVersionId)),
  ])
  if (!fromSnap.exists() || !toSnap.exists()) {
    throw new Error('Version not found for diff')
  }
  const fromSchema = fromSnap.data() as DeviceTypeSchemaSnapshot
  const toSchema = toSnap.data() as DeviceTypeSchemaSnapshot
  const fromFields = new Set(fieldsOf(fromSchema))
  const toFields = new Set(fieldsOf(toSchema))

  const addedFields = Array.from(toFields).filter((field) => !fromFields.has(field)).sort()
  const removedFields = Array.from(fromFields).filter((field) => !toFields.has(field)).sort()
  const changedAliases: string[] = []

  toSchema.canonicalFields.forEach((field) => {
    const oldField = (fromSchema.canonicalFields || []).find((item) => item.name === field.name)
    if (!oldField) return
    const oldAliases = new Set(oldField.aliases || [])
    const newAliases = new Set(field.aliases || [])
    if (oldAliases.size !== newAliases.size) {
      changedAliases.push(field.name)
      return
    }
    const same = Array.from(oldAliases).every((alias) => newAliases.has(alias))
    if (!same) changedAliases.push(field.name)
  })

  return { addedFields, removedFields, changedAliases: changedAliases.sort() }
}

export async function rollbackPublishedDeviceType(
  deviceType: string,
  versionId: string,
  actor = 'system'
): Promise<DeviceTypeSchemaSnapshot> {
  assertFirestoreEnvironmentForWrite('rollbackPublishedDeviceType')
  const db = ensureFirestore()
  const refs = getRefs()
  return runTransaction(db, async (tx) => {
    const versionRef = refs.version(deviceType, versionId)
    const versionSnap = await tx.get(versionRef)
    if (!versionSnap.exists()) throw new Error(`Version ${versionId} not found`)
    const version = versionSnap.data() as DeviceTypeSchemaSnapshot
    const timestamp = nowIso()
    const publishSnapshot: DeviceTypeSchemaSnapshot = {
      ...version,
      publishedAt: timestamp,
      publishedBy: actor,
      status: 'published',
    }
    tx.set(refs.published(deviceType), publishSnapshot, { merge: true })
    tx.set(refs.doc(deviceType), {
      deviceType,
      publishedVersionId: versionId,
      updatedAt: timestamp,
      publishedUpdatedAt: timestamp,
      lastRollbackBy: actor,
      lastRollbackAt: timestamp,
    }, { merge: true })
    return publishSnapshot
  })
}

export async function updateDeviceTypeIconSlots(
  deviceType: string,
  iconSlots: Array<{ id: string; dataUrl?: string }>,
  actor = 'system'
): Promise<Array<{ id: string; dataUrl?: string }>> {
  assertFirestoreEnvironmentForWrite('updateDeviceTypeIconSlots')
  const refs = getRefs()
  const timestamp = nowIso()
  const normalizedSlots = normalizeDeviceTypeIconSlots(iconSlots, undefined, undefined)
    .slice(0, ICON_SLOT_COUNT)
  const slot1DataUrl = toTrimmedString(
    normalizedSlots.find((slot) => slot.id === DEFAULT_ICON_SLOT_ID)?.dataUrl
  )

  await setDoc(refs.doc(deviceType), {
    deviceType,
    iconSlots: normalizedSlots,
    iconUploads: normalizedSlots.map((slot) => ({
      id: slot.id,
      dataUrl: toTrimmedString(slot.dataUrl),
    })),
    defaultIconSlotId: DEFAULT_ICON_SLOT_ID,
    // Keep legacy field in sync so older UI still resolves slot1.
    iconImageDataUrl: slot1DataUrl || null,
    updatedAt: timestamp,
    updatedBy: actor,
  }, { merge: true })

  return normalizedSlots
}

export async function updateDeviceTypeDisplayName(
  deviceType: string,
  displayName: string,
  actor = 'system'
): Promise<void> {
  assertFirestoreEnvironmentForWrite('updateDeviceTypeDisplayName')
  const refs = getRefs()
  const timestamp = nowIso()
  const normalizedName = displayName.trim()
  if (!normalizedName) {
    throw new Error('Device Type Name cannot be empty')
  }

  // New Device Types domain metadata
  await setDoc(refs.doc(deviceType), {
    deviceType,
    displayName: normalizedName,
    updatedAt: timestamp,
    updatedBy: actor,
  }, { merge: true })

  // Backward compatibility: keep existing Templates display-name source in sync.
  const db = ensureFirestore()
  await setDoc(
    doc(collection(doc(db, 'appfactory', 'templates'), 'deviceTypeDisplayNames'), deviceType),
    { en: normalizedName, updatedAt: timestamp },
    { merge: true }
  )
}

